import React, { useContext, lazy, Suspense, startTransition, useEffect, useState } from 'react'
import { Stack } from '@fluentui/react'

import styles from "./Chat.module.css";

import { AppStateContext } from "../../state/AppProvider";
import { useSpeech } from '../../hooks/useSpeech';
import { TalkingHeadContext, TalkingHeadContextType } from '../../state/TalkingHeadProvider'
import { AnimationsEnum, AnimationsTalkingArray } from '../../constants/talkingHead'
import Avatar from '../avatar/Avatar';
import { useDebug } from '../../hooks/useDebug';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ChatList } from './ChatList';
import { Tooltip } from '../tooltip/Tooltip';
import ModelPreviewBigMobile from '../../assets/modelPreviewBigMobile.png';
import ModelPreviewBig from '../../assets/modelPreviewBig.png';
import FullScreenIcon from '../../assets/zoomOut.svg';
import MinimizeIcon from '../../assets/zoomIn.svg';
import SoundOff from '../../assets/soundOff.svg';
import SoundOn from '../../assets/soundOn.svg';
import CloseIcon from '../../assets/close.svg';
import { useKeyboard } from '../../hooks/useKeyboard';

interface ChatProps {   
  fullScreen?: boolean;
  closeChat: () => void;
  setFullScreen?: (show: boolean) => void;
}

const Chat = React.memo(({fullScreen, closeChat, setFullScreen}: ChatProps) => {

  const isMobile = useIsMobile(900);
  const {isKeyboardOpen, keyboardHeight} = useKeyboard();

  const [firstFramePainted, setFirstFramePainted] = useState(false);
  const [hideTooltip, setHideTooltip] = useState(false);
  const [allowDefaultSpeech, setAllowDefaultSpeech] = useState(false);
  const appStateContext = useContext(AppStateContext)
  const isGeneratingAnswer = appStateContext?.state.isGeneratingAnswer;

  const debug = useDebug({ debugMode: appStateContext?.state.frontendSettings?.debug })
  const {
    visemes: { addViseme },
    animation: {
      animation,
      setAnimation: setAnimationForTalkingHead,
      interrupt: setInterruptedAnimationForTalkingHead,
    },
    talking: { isTalking, setTalking },
    speech:{
      isTextToSpeechMuted,
      setIsTextToSpeechMuted,
    }
  } = useContext(TalkingHeadContext) as TalkingHeadContextType

  const {
    isSpeechEnabled,
    terminateTextToSpeech
  } = useSpeech({        
    onSpeechStarted: (startTime) => {
    debug(() => console.log("Speech started"))
    setTalking(true, { actualStartTime: startTime })
    },
    onSpeechFinished: () => {
    debug(() => console.log("Speech finished"))
    setTalking(false)
    },
    onVisemeReceived: (id, offset, visemeId, animation) => {
    debug(() => console.log("Viseme received: ", animation))
    addViseme({ id, visemeId, offset, animation })
    },
  });

  const toggleSpeaking = (turnOffSound?: boolean) => {
    setIsTextToSpeechMuted((prevState: boolean) => {
      if (prevState) {
        if (!isGeneratingAnswer) setAnimationForTalkingHead(AnimationsEnum.Idle3)
      } else {
        terminateTextToSpeech()

        if (!isGeneratingAnswer) setInterruptedAnimationForTalkingHead()
      }

      return turnOffSound || !prevState
    })
  }

  const onCloseChat = async() => {
    await toggleSpeaking(true)
    closeChat()
  }

  useEffect(() => {
    setIsTextToSpeechMuted(true)
    setAllowDefaultSpeech(true)
    setTimeout(() => {
      setHideTooltip(true)
    }, 5000)
    return () => {
      setIsTextToSpeechMuted(true)
      terminateTextToSpeech()
    }
  }, []);

  useEffect(() => {
    if (isTalking) {
      if (!AnimationsTalkingArray.includes(animation)){
        setAnimationForTalkingHead(AnimationsEnum.TalkStarted)
      }
    }
  }, [isTalking]);

  // control for body scroll
  useEffect(() => {
    if (fullScreen) {
      document.body.classList.add("leora-no-scroll");
    } else {
      document.body.classList.remove("leora-no-scroll");
    }
    return () => {
      document.body.classList.remove("leora-no-scroll");
    };
  }, [fullScreen]);


  useEffect(() => {
    const preventScroll = (e: any) => {
      e.preventDefault();
    };

    if (isMobile && isKeyboardOpen) {
      document.documentElement.addEventListener('touchmove', preventScroll, { passive: false });
      const chat = document.querySelector('[id="chat-overlay"]');
      if (chat) {
        chat.scrollTo({
          top: chat.scrollHeight,
          behavior: 'smooth'
        });
      }
    } else {
      document.documentElement.removeEventListener('touchmove', preventScroll);
    }
    return () => document.documentElement.removeEventListener('touchmove', preventScroll);
  }, [isKeyboardOpen, isMobile]);

  const [showAvatar, setShowAvatar] = useState(false);
  useEffect(() => {
    startTransition(() => setShowAvatar(true));
  }, []);

  return (
    <div 
      className={isMobile ? styles.containerMobile : styles.container} 
    >
        <Stack className={isMobile ? styles.chatRootMobile : styles.chatRoot}>
          {!isMobile && 
            <div 
              className={`${styles.twistBox} ${fullScreen ? styles.chatBackgroundBig : styles.chatBackgroundSmall}`} 
            >
              <div className={styles.rectangle}></div>
            </div>
          }
          {fullScreen && <div 
            className = {`${styles.previewAvatar} ${firstFramePainted ? styles.hidden : ""}`}
            style={isMobile && isKeyboardOpen ? {bottom: `-${keyboardHeight}px`} : {}}
          >
            <img src={isMobile ? ModelPreviewBigMobile : ModelPreviewBig} alt="Leora avatar"/>
          </div>}
          <Suspense fallback={null}>
            {showAvatar && <Avatar fullScreen={fullScreen} scale={1} firstFrameReady={firstFramePainted} keyboardHeight={keyboardHeight} onFirstFrame={() => requestAnimationFrame(() => setFirstFramePainted(true))}/>}
          </Suspense>
          <Stack  
            className={isMobile ? styles.chatBlockMobile : fullScreen ? styles.chatBlock : styles.chatBlockSmall}
          >
            <ChatList 
              fullScreen={fullScreen}
              hideTooltip={hideTooltip}
              setHideTooltip={setHideTooltip}
              toggleSpeaking={toggleSpeaking}
              allowDefaultSpeech={allowDefaultSpeech}
              setAllowDefaultSpeech={setAllowDefaultSpeech}
            />
          </Stack>
          <Stack 
            className={fullScreen ? styles.topButtonsPanel : styles.topButtonsPanelSmall}
            style={isMobile && isKeyboardOpen ? {top: `${keyboardHeight + 12}px`} : {}}
          >
            {!isMobile && setFullScreen && (
                <button className={styles.fullScreenButton}
                  aria-label="Full Screen button"
                  onClick={()=>setFullScreen(!fullScreen)}
                >
                  <img src={fullScreen ? MinimizeIcon : FullScreenIcon} alt="open full screen view icon" className={styles.icon} />
                  <span 
                    className={styles.fullScreenTitle}
                    
                  >{fullScreen ? 'Minimize' : 'Full Screen'}</span>
                </button>
            )}
            <Stack horizontal tokens={{ childrenGap: 10}} style={{ marginLeft: 'auto'}}>
              {isSpeechEnabled && isMobile &&
                <div>
                  {!hideTooltip && isTextToSpeechMuted && <Tooltip 
                    text="Click to let me speak up! I'm ready to chat!"
                    onClick={() => setHideTooltip(true)}
                    isTop={!isMobile}
                    right={55}
                    bottom={isMobile ? -60 : 35}
                    width={isMobile ? 175 : 195}
                    padding={isMobile ? '0px 10px' : '10px'}
                    margin={isMobile ? '0px 0px 8px' : '0px 0px 8px'}
                  />}
                <button className={styles.actionButton}
                  aria-label="Allow speech button"
                  onClick={() => toggleSpeaking()}
                >
                  {isTextToSpeechMuted ?
                    <img src={SoundOff} alt="sound off" className={styles.iconButton} />
                    :
                    <img src={SoundOn} alt="sound on" className={styles.iconButton} />
                  }
                </button>
              </div>
              }
              <button className={styles.actionButton}
                aria-label="Close button"
                onClick={onCloseChat}
              >
                <img src={CloseIcon} alt="close" className={styles.iconButton} />
              </button>
            </Stack>
          </Stack>
        </Stack>
    </div>
  );
});

export default Chat;
